import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/**
 * De opmaakwerkbalk van een rich-text-veld, als WAI-ARIA-toolbar (APG "Toolbar" en "Menu Button",
 * R-14, R-139, R-142).
 *
 * **Eén rij met groepen en drie menu's** (ontwerp van het invulscherm, 29 oktober 2026). Tot nu
 * stonden alle 29 knoppen naast elkaar en liep de balk over vier rijen. Nu staat wat een schrijver
 * steeds gebruikt los in de rij (vet, cursief, onderstrepen, de twee lijsten, "Bron citeren" en de
 * opmerking), en zit de rest achter drie menuknoppen: "Tekst" (gewone tekst en de koppen),
 * "Invoegen" (formule, figuur, tabel, lijn, bronvermelding) en "Meer opmaak". Elke handeling van
 * vóór deze wijziging is nog bereikbaar; geen enkele is weggevallen.
 *
 * **Eén tabstop, pijltjes binnen de balk.** De balk is één stop in de tabvolgorde (roving tabindex):
 * links en rechts gaan naar het vorige en volgende element, Home en End naar het eerste en laatste.
 * Een menuknop opent met Enter, spatie of pijl omlaag; in het menu lopen pijl omhoog en omlaag rond,
 * Escape sluit het en zet de focus terug op de menuknop, Tab sluit het en gaat verder.
 *
 * **Op een smal veld één rij met een overloopmenu** (audit R-19, ontwerp "Telefoon"). Is de balk
 * smaller dan `NARROW_PX`, dan blijven alleen de handelingen uit `smal.houden` in de rij (vet, cursief,
 * opsomming, Bron citeren) en staat al het andere, ook de inhoud van de drie menu's, in één menu
 * "Meer opmaak en invoegen", per groep gescheiden. Er valt niets weg: de controls volgen de POC
 * (eisenboek 12.9), dit is alleen een andere plek. Gemeten op de breedte van het veld en niet van het
 * scherm, zodat een smalle kolom op een tablet ook past.
 *
 * **De muis haalt de focus niet weg uit de tekst.** Elk element zet `preventDefault` op de
 * mousedown, zoals CitatenPOC's `ToolButton`: anders verliest het veld zijn selectie vóór de klik,
 * en dan werkt "Vet" op niets.
 */

/** Een handeling in de balk of in een menu. */
export interface ToolbarAction {
  readonly key: string
  /** De toegankelijke naam; bij een knop met zichtbare tekst begint die tekst ermee (WCAG 2.5.3). */
  readonly label: string
  readonly icon?: ReactNode
  /** Zichtbare tekst naast of in plaats van het icoon. */
  readonly text?: string
  /** Aan of uit (vet, een lijst); `undefined` is een handeling zonder stand (invoegen). */
  readonly pressed?: boolean
  /** Kortere zichtbare tekst op een smal veld ("Bron"); de naam blijft het label. */
  readonly shortText?: string
  /** Een keuze uit een rij (de soort tekst) in een gemengd menu: `menuitemradio`. */
  readonly radio?: boolean
  /** Het eerste item van een nieuwe groep in een gemengd menu: er staat een scheiding boven. */
  readonly newGroup?: boolean
  readonly onRun: () => void
}

/** Een menuknop met zijn items. */
export interface ToolbarMenu {
  readonly key: string
  readonly label: string
  readonly text: string
  /** `radio` voor een keuze uit een rij (de soort tekst), anders losse items. */
  readonly kind?: 'radio'
  readonly items: readonly ToolbarAction[]
}

export type ToolbarPart = ToolbarAction | ToolbarMenu | 'scheiding'

function isMenu(part: ToolbarAction | ToolbarMenu): part is ToolbarMenu {
  return 'items' in part
}

export interface ToolbarProps {
  readonly label: string
  readonly controls: string
  readonly parts: readonly ToolbarPart[]
  /** De vorm op een smal veld: welke handelingen in de rij blijven, en het menu voor de rest. */
  readonly smal?: { readonly houden: readonly string[]; readonly label: string; readonly text: string }
}

/** Onder deze breedte van de balk (een veld op een telefoon) gaat de balk naar zijn smalle vorm. */
const NARROW_PX = 360

/** De smalle vorm: wat blijft in volgorde, en één menu met de rest per groep. */
function narrowParts(parts: readonly ToolbarPart[], narrow: NonNullable<ToolbarProps['smal']>): readonly ToolbarPart[] {
  const keep = new Set(narrow.houden)
  const kept: ToolbarPart[] = []
  const rest: ToolbarAction[] = []
  let group = false
  for (const part of parts) {
    if (part === 'scheiding') {
      group = true
      continue
    }
    if (!isMenu(part) && keep.has(part.key)) {
      kept.push(part.shortText === undefined ? part : { ...part, text: part.shortText })
      continue
    }
    const entries = isMenu(part) ? part.items.map((item) => (part.kind === 'radio' ? { ...item, radio: true } : item)) : [part]
    entries.forEach((entry, index) => {
      rest.push(index === 0 && (group || isMenu(part)) && rest.length > 0 ? { ...entry, newGroup: true } : entry)
    })
    group = false
  }
  return rest.length === 0 ? kept : [...kept, { key: 'smal-meer', label: narrow.label, text: narrow.text, items: rest }]
}

export function Toolbar({ label, controls, parts: wideParts, smal: narrow }: ToolbarProps): ReactNode {
  const bar = useRef<HTMLDivElement | null>(null)
  const [isNarrow, setNarrow] = useState(false)
  useLayoutEffect(() => {
    const element = bar.current?.parentElement
    if (narrow === undefined || element === null || element === undefined) return
    const measure = (): void => setNarrow(element.getBoundingClientRect().width < NARROW_PX)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [narrow])
  const parts = narrow !== undefined && isNarrow ? narrowParts(wideParts, narrow) : wideParts
  const items = parts.filter((part): part is ToolbarAction | ToolbarMenu => part !== 'scheiding')
  const [current, setCurrent] = useState(0)
  const [openMenu, setOpenMenu] = useState<string | undefined>(undefined)
  const refs = useRef(new Map<string, HTMLButtonElement>())
  const place = Math.min(current, items.length - 1)

  const focusAt = useCallback(
    (index: number) => {
      const wrapped = (index + items.length) % items.length
      setCurrent(wrapped)
      const key = items[wrapped]?.key
      if (key !== undefined) refs.current.get(key)?.focus()
    },
    [items],
  )

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    // Alleen voor de elementen van de balk zelf; een open menu handelt zijn eigen toetsen af.
    if ((event.target as HTMLElement).closest('[role="menu"]') !== null) return
    if (event.key === 'ArrowRight') focusAt(place + 1)
    else if (event.key === 'ArrowLeft') focusAt(place - 1)
    else if (event.key === 'Home') focusAt(0)
    else if (event.key === 'End') focusAt(items.length - 1)
    else return
    event.preventDefault()
  }

  let index = -1
  return (
    <div ref={bar} className="ef-richtext__balk" role="toolbar" aria-label={label} aria-controls={controls} onKeyDown={onKeyDown} data-smal={narrow !== undefined && isNarrow ? 'ja' : undefined}>
      {parts.map((part, partIndex) => {
        if (part === 'scheiding') {
          // eslint-disable-next-line react/no-array-index-key
          return <span key={`scheiding-${partIndex}`} className="ef-richtext__scheiding" aria-hidden="true" />
        }
        index += 1
        const own = index
        const tabIndex = own === place ? 0 : -1
        const register = (element: HTMLButtonElement | null): void => {
          if (element === null) refs.current.delete(part.key)
          else refs.current.set(part.key, element)
        }
        if (isMenu(part)) {
          return (
            <MenuButton
              key={part.key}
              menu={part}
              tabIndex={tabIndex}
              register={register}
              open={openMenu === part.key}
              onOpen={(open) => {
                setCurrent(own)
                setOpenMenu(open ? part.key : undefined)
              }}
            />
          )
        }
        return (
          <button
            key={part.key}
            ref={register}
            type="button"
            className={part.text === undefined ? 'ef-richtext__knop' : 'ef-richtext__knop ef-richtext__knop--gelabeld'}
            tabIndex={tabIndex}
            {...(part.pressed === undefined ? {} : { 'aria-pressed': part.pressed })}
            // Een knop met zichtbare tekst draagt die tekst als naam; een knop met alleen een icoon, of
            // met een kortere tekst dan zijn naam ("Bron" voor "Bron citeren"), krijgt zijn naam uit het label.
            {...(part.text === undefined || part.text !== part.label ? { 'aria-label': part.label, title: part.label } : {})}
            onMouseDown={keepSelection}
            onFocus={() => setCurrent(own)}
            onClick={part.onRun}
          >
            {part.icon}
            {part.text}
          </button>
        )
      })}
    </div>
  )
}

function keepSelection(event: ReactMouseEvent<HTMLElement>): void {
  event.preventDefault()
}

function MenuButton({
  menu,
  tabIndex,
  register,
  open,
  onOpen,
}: {
  menu: ToolbarMenu
  tabIndex: number
  register: (element: HTMLButtonElement | null) => void
  open: boolean
  onOpen: (open: boolean) => void
}): ReactNode {
  const menuId = useId()
  const button = useRef<HTMLButtonElement | null>(null)
  const list = useRef<HTMLDivElement | null>(null)
  /** Welk item de focus krijgt bij openen: het eerste, het laatste (pijl omhoog) of het gekozen. */
  const start = useRef<'eerste' | 'laatste' | 'gekozen'>('eerste')
  // Via een ref: de aanroeper geeft per render een nieuwe functie, en het effect hieronder hoort
  // alleen bij het openen te lopen, niet bij elke hertekening (dan sprong de focus terug).
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen

  useEffect(() => {
    if (!open) return
    const entries = [...(list.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])]
    const chosen = entries.find((entry) => entry.getAttribute('aria-checked') === 'true')
    const target =
      start.current === 'laatste' ? entries.at(-1) : start.current === 'gekozen' && chosen !== undefined ? chosen : entries[0]
    target?.focus()
    // Een klik buiten het menu sluit het, zoals bij elke keuzelijst.
    const outside = (event: MouseEvent): void => {
      const node = event.target as Node
      if (list.current?.contains(node) === true || button.current?.contains(node) === true) return
      onOpenRef.current(false)
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [open])

  const close = (refocus: boolean): void => {
    onOpen(false)
    if (refocus) button.current?.focus()
  }

  const onMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const entries = [...(list.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])]
    const at = entries.indexOf(document.activeElement as HTMLElement)
    if (event.key === 'ArrowDown') entries[(at + 1) % entries.length]?.focus()
    else if (event.key === 'ArrowUp') entries[(at - 1 + entries.length) % entries.length]?.focus()
    else if (event.key === 'Home') entries[0]?.focus()
    else if (event.key === 'End') entries.at(-1)?.focus()
    else if (event.key === 'Escape') close(true)
    else if (event.key === 'Tab') {
      // Tab verlaat het menu en de balk; geen `preventDefault`, de browser gaat verder.
      close(false)
      return
    } else return
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <span className="ef-richtext__menuplek">
      <button
        ref={(element) => {
          button.current = element
          register(element)
        }}
        type="button"
        className="ef-richtext__knop ef-richtext__knop--menu"
        tabIndex={tabIndex}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        // De naam begint met wat het menu is en draagt de zichtbare tekst (WCAG 2.5.3): "Meer
        // opmaak" bij "Meer", "Soort tekst: Kop 2" bij "Kop 2".
        // Een teken als "⋯" is geen tekst die iemand uitspreekt; dan is het label de hele naam.
        aria-label={menu.label.includes(menu.text) || !/\p{L}/u.test(menu.text) ? menu.label : `${menu.label}: ${menu.text}`}
        title={menu.label}
        onMouseDown={keepSelection}
        onClick={() => {
          start.current = menu.kind === 'radio' ? 'gekozen' : 'eerste'
          onOpen(!open)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            start.current = event.key === 'ArrowUp' ? 'laatste' : menu.kind === 'radio' ? 'gekozen' : 'eerste'
            onOpen(true)
            event.preventDefault()
          }
        }}
      >
        {menu.text}
        <span className="ef-richtext__menupijl" aria-hidden="true" />
      </button>
      {open ? (
        <div ref={list} id={menuId} role="menu" aria-label={menu.label} className="ef-richtext__menu" onKeyDown={onMenuKey}>
          {menu.items.map((item) => [
            item.newGroup === true ? <div key={`${item.key}-scheiding`} role="separator" className="ef-richtext__menuscheiding" /> : null,
            <button
              key={item.key}
              type="button"
              className="ef-richtext__menuitem"
              role={menu.kind === 'radio' || item.radio === true ? 'menuitemradio' : item.pressed === undefined ? 'menuitem' : 'menuitemcheckbox'}
              {...(item.pressed === undefined ? {} : { 'aria-checked': item.pressed })}
              tabIndex={-1}
              onMouseDown={keepSelection}
              onClick={() => {
                close(false)
                item.onRun()
              }}
            >
              <span className="ef-richtext__menuicoon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </button>,
          ])}
        </div>
      ) : null}
    </span>
  )
}
